
import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, AlertCircle } from 'lucide-react';
import { toast } from "sonner";
import MainLayout from '@/components/layout/MainLayout';
import SubscriptionTable from '@/components/subscriptions/SubscriptionTable';
import SubscriptionFilters, { SubscriptionFilters as Filters } from '@/components/subscriptions/SubscriptionFilters';
import TablePagination from '@/components/ui/TablePagination';
import ExportDropdown from '@/components/subscriptions/ExportDropdown';
import { Button } from '@/components/ui/button';
import { useSubscriptionList } from '@/hooks/useSubscription';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';

const Subscriptions = () => {
  const { translate } = useLanguage();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customerId');
  
  // Fix: Added key to ensure component re-mounts when customerId changes
  const { 
    subscriptions, 
    filteredCount,
    isLoading, 
    setFilters,
    error,
    pagination 
  } = useSubscriptionList(customerId || undefined);
  
  const handleFilterChange = (filters: Filters) => {
    setFilters(filters);
    // Reset to first page when filters change
    pagination.setPage(1);
  };

  // Reset to first page when customerId changes (once, not repeatedly)
  useEffect(() => {
    pagination.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // Display error toast when there's an error (once, not repeatedly)
  useEffect(() => {
    if (error) {
      toast.error("Failed to load subscriptions. Please try again later.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <MainLayout>
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{translate('subscriptions.title')}</h1>
          <p className="text-muted-foreground">
            {customerId 
              ? translate('subscriptions.customerViewSubtitle')
              : translate('subscriptions.subtitle')
            }
          </p>
        </div>
        <div className="flex gap-2">
          {!customerId && user?.email && (
            <ExportDropdown userEmail={user.email} />
          )}
          <Link to="/subscriptions/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              {translate('subscriptions.newSubscription')}
            </Button>
          </Link>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{translate('common.error')}</AlertTitle>
          <AlertDescription>
            {translate('subscriptions.loadingError')}
          </AlertDescription>
        </Alert>
      )}

      {!customerId && (
        <SubscriptionFilters onFilterChange={handleFilterChange} className="mb-6" />
      )}

      <SubscriptionTable 
        subscriptions={subscriptions}
        isLoading={isLoading}
        className="mb-6"
      />
      
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-muted-foreground">
          {translate('subscriptions.showing')} {subscriptions.length > 0 ? (pagination.page - 1) * pagination.pageSize + 1 : 0} - {Math.min(pagination.page * pagination.pageSize, filteredCount)} {translate('subscriptions.of')} {filteredCount} {translate('subscriptions.subscriptions')}
        </div>
        
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={pagination.setPage}
        />
      </div>
    </MainLayout>
  );
};

export default Subscriptions;
